import { useEffect, useState } from 'react';
import { ExternalLink, Star, ChevronDown } from 'lucide-react';
import externalAPI from './ExternalAPI';
import { useCurrency } from '../Hooks/useCurrency';
import { formatDate, getThreadNameFromTypeID } from '../Utils/Utilities';
import { Textarea } from '../Components/UI/textarea';
import { format } from 'date-fns';
import { toast } from 'sonner';
import CustomSearchableDropdown from '../Components/Common/CustomSearchableDD';

const Feedback = () => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasExistingFeedback, setHasExistingFeedback] = useState(false);
  const [selectedRejectionReason, setSelectedRejectionReason] = useState(null);
  const [rejectionReasonOptions, setRejectionReasonOptions] = useState([]);
  const [orderInfo, setOrderInfo] = useState({
    name: "",
    orderID: null,
    totalAmount: null,
    productName: null
  });
  const [urlParams, setUrlParams] = useState({
    agencyID: null,
    threadID: null,
    rating: 0
  });
  const [countryID, setcountryID] = useState("");
  const currencySymbol = useCurrency(countryID);
  const [threadTypes, setThreadTypes] = useState([]);
  const [googleRevLink, setGoogleRevLink] = useState("");

  // Get URL parameters
  const getURLParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      agencyID: urlParams.get('aid'),
      threadID: urlParams.get('tid'),
      rating: urlParams.get('rating'),
    };
  };

  // Parse rejection reasons based on thread type
  const parseRejectionReasons = (rejRsns, typeID) => {
    if (!rejRsns || !typeID) return [];

    try {
      const masterData = JSON.parse(rejRsns);
      const threadReasons = masterData.find(item => item.TID === typeID);

      if (!threadReasons) return [];

      const reasons = JSON.parse(threadReasons.Rsns);
      return reasons.map(reason => ({
        value: reason.R ,
        label: reason.R,
      }));
    } catch (error) {
      console.error("Error parsing failure reasons:", error);
      return [];
    }
  };

  // Load data on component mount
  useEffect(() => {
    const loadFeedbackData = async () => {
      try {
        // Get URL parameters
        const params = getURLParameters();
        setUrlParams(params);
        setRating(params.rating);

        // Validate parameters
        if (!params.agencyID || !params.threadID) {
          return;
        }

        const threadData = {
          agencyID: params.agencyID,
          threadID: params.threadID
        }
        // Fetch feedback details
        const result = await externalAPI.getFeedbackDetails(threadData,  'FDBK');

        // Parse the company info from the result
        if (result && result.parsedDataSet) {
          
          const { Table1, Table2 } = result.parsedDataSet;
          
          // Extract data safely
          const agencyData = Table1?.[0] || {};
          const orderData = Table2?.[0] || {};

          agencyData?.CC && setcountryID(agencyData.CountryID);
          agencyData?.GoogleLink && setGoogleRevLink(agencyData.GoogleLink);
          
          if (agencyData?.ThreadTypes) {
            try {
              setThreadTypes(JSON.parse(agencyData.ThreadTypes));
            } catch (err) {
              console.warn('Failed to parse ThreadTypes JSON', err);
            }
          }

          // Set order information
          setOrderInfo({
            name: agencyData.AgencyName || "",
            orderID: orderData.Reference || null,
            connID: orderData.ConnID,
            typeID: orderData.TypeID,
            totalAmount: orderData.BizProspect ? `${orderData.BizProspect.toLocaleString()}` : null,
            productName: orderData.MainProduct || null,
            received: orderData.SinceDT,
            finished: orderData.DoneDT,
          });

          // Parse rejection reasons if available
          if (agencyData.RejRsns && orderData.TypeID) {
            const reasons = parseRejectionReasons(agencyData.RejRsns, orderData.TypeID);
            setRejectionReasonOptions(reasons);
          }

          // Prefill form with existing feedback if available
          if (orderData.FdbkScore && orderData.FdbkScore > 0) {
            setRating(orderData.FdbkScore);
            setHasExistingFeedback(true);
            setSubmitted(true);
          }
          if (orderData.FdbkNotes) {
            setFeedback(orderData.FdbkNotes);
          }
          // Prefill rejection reason if exists
          if (orderData.RejRsnID) {
            setSelectedRejectionReason(orderData.RejRsnID);
          }

          if (orderData.FailureReason) {
            setSelectedRejectionReason(orderData.FailureReason);
          }

        }
        
      } catch (error) {
        console.error('Error loading feedback data:', error);
      }
    };

    loadFeedbackData();
  }, []);

  const ratings = [
    { 
      label: 'Very Bad', 
      color: 'sahi-red',
      question: "Oops! Please help us correct!"
    },
    { 
      label: 'Bad', 
      color: 'sahi-orange',
      question: "What went wrong? Help us improve!"
    },
    { 
      label: 'Okay', 
      color: 'sahi-dark-blue',
      question: "How can we make it better?"
    },
    { 
      label: 'Good', 
      color: 'sahi-blue',
      question: "How can we do even better?"
    },
    { 
      label: 'Excellent', 
      color: 'sahi-green',
      question: "What did you love most about our service?"
    }
  ];

  const handleStarClick = (starIndex) => {
    if (!hasExistingFeedback) {
      setRating(starIndex);
      // Reset rejection reason when rating changes
      if (starIndex > 3) {
        setSelectedRejectionReason(null);
      }
    }
  };

  const handleStarHover = (starIndex) => {
    if (!hasExistingFeedback) {
      setHoverRating(starIndex);
    }
  };

  const handleStarLeave = () => {
    if (!hasExistingFeedback) {
      setHoverRating(0);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.success('Please select a rating.');
      return;
    }

    // Check if rejection reason is required and selected
    if (rating <= 3 && !selectedRejectionReason) {
      toast.success('Please select a failure reason.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare feedback data in DataTable format as expected by C# backend
      const feedbackData = {
        agencyID: parseInt(urlParams.agencyID) || 0,
        connID: parseInt(orderInfo.connID) || 0,
        threadID: parseInt(urlParams.threadID) || 0,
        fdbkScore: rating,
        fdbkNotes: feedback || "",
        rejReason: rating <= 3 ? selectedRejectionReason : '',
        type: "FDBK"
      }

      const result = await externalAPI.setFeedbackDetails(feedbackData, 'FDBK');

      if (result.error === false && result.message === "Success") {
        setHasExistingFeedback(true);
        setIsSubmitting(false);
        setSubmitted(true);
      } else {
        toast.success(result.message);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
      setIsSubmitting(false);
    }
  };

  const getCurrentQuestion = () => {
    if (rating > 0) {
      return ratings[rating - 1].question;
    }
    return null;
  };

  // Check if rejection reason is required
  const isRejectionReasonRequired = rating > 0 && rating <= 3;

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg md:p-8 p-6 w-full max-w-2xl text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-sahi-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h2 className="md:text-4xl text-2xl font-semibold text-gray-800 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-4 text-sm md:text-base">Your feedback has been submitted.</p>
        </div>
        <div className="rounded-lg w-full max-w-2xl text-center">
          {(rating === 4 || rating === 5) && googleRevLink && (
            <div className="bg-sahi-white shadow-lg p-6 rounded-lg mt-6 flex items-center justify-between md:flex-row flex-col gap-4">
              <p className="text-gray-700 md:text-base text-sm">Would you like to give feedback on Google Reviews?</p>
              <button
                onClick={() => window.open(googleRevLink, "_blank")}
                className="bg-sahi-primary-color text-white font-medium py-2 px-5 rounded-lg flex items-center gap-2 md:w-auto w-full justify-center"
              >
                Google Reviews
                <ExternalLink className="w-4 h-4 ml-1" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }



  return (
    <div className="md:min-h-screen h-full bg-gray-100 flex items-center justify-center md:p-4">
      <div className="bg-white rounded-lg md:shadow-lg p-8 md:px-8 px-6  w-full max-w-2xl md:border">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="md:text-4xl text-3xl font-semibold text-sahi-primary-color mb-2">Feedback</h1>
          <p className="text-gray-600 md:text-base text-xs  ">Please help us become your favourite supplier!</p>
        </div>

        {/* Company Information with Agency Details */}
        {orderInfo.name && (
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{orderInfo.name}</h3>
            {/* Mobile (shorter version) */}
            <p className="text-xs text-gray-400 mb-4 block md:hidden">
              We are committed to delivering exceptional products and services. Your feedback helps us maintain the highest standards.
            </p>

            {/* Desktop (full version) */}
            <p className="md:text-sm text-xs text-gray-400 mb-4 hidden md:block">
              We are committed to delivering exceptional products and services. Your feedback helps us maintain the highest standards and continue to exceed your expectations.
            </p>

            <div className="p-4 bg-gray-50 rounded-lg border">
              <h4 className="md:text-base text-sm font-medium text-gray-800 mb-2">
                {getThreadNameFromTypeID( orderInfo.typeID, threadTypes, orderInfo.typeID == 3 ? undefined : "SnSng")}
                <span className="font-semibold">
                  {orderInfo?.orderID && ` #${orderInfo.orderID}`}
                </span>
                {` Details`} 
              </h4>
              <div className="md:text-sm text-xs text-gray-600 space-y-1">
                {orderInfo.productName && (
                  <p><span className="font-semibold">Offering: </span> {orderInfo.productName}</p>
                )}
                {orderInfo.totalAmount && (
                  <p><span className="font-semibold">Amount:</span>{` ${currencySymbol} ${orderInfo.totalAmount}`}</p>
                )}
                {orderInfo.received && (
                  <p><span className="font-semibold">Received:</span>{` ${format(orderInfo.received, "dd-MMM-yy")}`}</p>
                )}
                {orderInfo.finished && (
                  <p><span className="font-semibold">Finished:</span>{` ${format(orderInfo.finished, "dd-MMM-yy")}`}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Constant Question */}
        <div className="mb-6">
          <p className="text-gray-700 font-medium mb-4 text-left md:text-base text-sm">How do you feel about working with us?</p>
          
          {/* Star Rating */}
          <div className="flex items-center space-x-4 mb-6">
            <div className="flex space-x-1">
              {[1, 2, 3, 4, 5].map((starIndex) => (
                <button
                  key={starIndex}
                  onClick={() => handleStarClick(starIndex)}
                  onMouseEnter={() => handleStarHover(starIndex)}
                  onMouseLeave={handleStarLeave}
                  className="transition-colors duration-200 focus:outline-none"
                  disabled={isSubmitting || hasExistingFeedback}
                >
                <Star
                  fill="currentColor"
                  className={`
                    h-8 w-8 sm:h-10 sm:w-10
                    ${starIndex <= (hoverRating || rating)
                      ? `fill-${ratings[(hoverRating || rating) - 1]?.color} text-${ratings[(hoverRating || rating) - 1]?.color}`
                      : 'text-gray-300'
                    }
                    hover:scale-110 transition-transform duration-200
                    ${(isSubmitting || hasExistingFeedback) ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                />
                </button>
              ))}
            </div>

            {/* Show selected rating label */}
            {(rating > 0 || hoverRating > 0) && (
              <div>
                <span className={`md:text-lg text-base font-medium text-${ratings[(hoverRating || rating) - 1]?.color}`}>
                  {ratings[(hoverRating || rating) - 1].label}
                </span>
              </div>
            )}
          </div>

          {/* Rejection Reason Dropdown - Show only for ratings 1-3 */}
          {isRejectionReasonRequired && rejectionReasonOptions.length > 0 && (
            <div className="mb-6">
              <CustomSearchableDropdown
                options={rejectionReasonOptions}
                value={selectedRejectionReason}
                onSelect={setSelectedRejectionReason}
                placeholder="Select Failure Reason"
                displayKey="label"
                valueKey="value"
                label="Failure Reason"
                emptyMessage="No failure reasons found"
                searchEnabled={false}
                disabled={hasExistingFeedback}
              />
            </div>
          )}
        </div>

        {/* Dynamic Question based on selected face */}
        {getCurrentQuestion() && (
          <div className="mb-4">
            <p className="text-gray-700 font-medium text-left md:text-base text-sm">{getCurrentQuestion()}</p>
          </div>
        )}

        {/* Response Textarea */}
        <div className="mb-6">
          <Textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Enter your feedback"
            className={`w-full h-24 px-3 py-2 border border-gray-300 rounded-md`}
            rows={4}
            disabled={isSubmitting || hasExistingFeedback}
          />
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || rating === 0 || hasExistingFeedback || (isRejectionReasonRequired && !selectedRejectionReason)}
          className={`w-full font-medium py-3 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 ring-${ratings[rating - 1]?.color} focus:ring-offset-2 flex items-center justify-center ${
            (isSubmitting || rating === 0 || hasExistingFeedback || (isRejectionReasonRequired && !selectedRejectionReason))
              ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
              : `bg-${ratings[rating - 1]?.color} text-white`
          }`}
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting...
            </>
          ) : hasExistingFeedback ? (
            <>
              <span className="mr-2">✓</span>
              Feedback Already Submitted
            </>
          ) : (
            <>
              <span className="mr-2">✓</span>
              Submit Feedback
            </>
          )}
        </button>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Powered by{' '}
            <a href="https://sahicrm.com/" className="text-sahi-green hover:underline">
              SahiClub.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Feedback;